import type { CqCheck, CqCompare } from '../api/connectionQuality'
import './CqPath.css'

/**
 * Verificarile ecranului Connection Quality: MTU, DNS, portal captiv / proxy,
 * VPN. Textul vine gata scris de pe server (Checks.php); aici doar se aseaza,
 * cu un semn pe langa culoare - cate un card, sus (30 septembrie 2026).
 */

const ICON: Record<CqCheck['state'], string> = {
  ok: 'fas fa-check-circle',
  warn: 'fas fa-exclamation-triangle',
  info: 'fas fa-info-circle',
  // Nu un cerc gol: arata ca o bifa de apasat, si nu e (29 septembrie 2026).
  none: 'fas fa-minus-circle',
}

/**
 * Panoul "Checks" ca lista - ramas numai pe tichet (TicketConnectionQuality).
 * Pe ecranul aparatului verificarile stau sus, cate un card (CqCheckCards).
 */
export function CqChecks({ checks }: { checks: CqCheck[] }) {
  return (
    <div className="cq-checks">
      <h5>Checks</h5>
      <div className="cq-checks-hint">{CHECKS_HINT}</div>
      <ul>
        {checks.map((c) => (
          <li key={c.kind} className={`is-${c.state}`} data-check={c.kind}>
            <i className={ICON[c.state]} aria-hidden="true"></i>
            <span className="cq-check-name">{c.name}</span>
            <span className="cq-check-text">{c.text}</span>
            {c.at ? <span className="cq-check-at">{c.at}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

export const CHECKS_HINT =
  'Measured by the agent on its own: a few minutes after Connection Quality is switched on, then every 6 hours and when the network changes. Nothing to tick here.'

/** Cele patru verificari, in ordinea serverului (Checks::KINDS). */
export const CHECK_KINDS: ReadonlyArray<{ kind: CqCheck['kind']; name: string }> = [
  { kind: 'mtu', name: 'MTU' },
  { kind: 'dns', name: 'DNS' },
  { kind: 'captive', name: 'Captive portal / proxy' },
  { kind: 'vpn', name: 'VPN' },
]

export type CqCheckCard = {
  kind: CqCheck['kind']
  title: string
  state: CqCheck['state']
  stateText: string
  /** Valoarea mare a cardului: "1480", "3 servers", "None detected", "Active". */
  value: string
  /** Randul mic de sub ea: adaptorul VPN, serverele DNS, "no fragmentation". */
  detail: string
  at: string | null
  /** Textul intreg de pe server, ca titlu la hover. */
  text: string
}

const CHECK_STATE_TEXT: Record<CqCheck['state'], string> = { ok: 'ok', warn: 'attention', info: 'info', none: 'not measured yet' }

/**
 * Textul gata scris de server (Checks::read) desfacut in valoare si detaliu.
 * Formele sunt fixe acolo; ce nu se recunoaste ramane intreg, ca valoare.
 */
export function checkCard(c: CqCheck): CqCheckCard {
  const base = { kind: c.kind, title: c.name, state: c.state, stateText: CHECK_STATE_TEXT[c.state], at: c.at, text: c.text }
  if (c.state === 'none') return { ...base, value: '-', detail: '' }
  const text = c.text.trim()
  switch (c.kind) {
    case 'mtu': {
      const mtu = /MTU (\d+)/.exec(text)?.[1] ?? null
      if (c.state === 'ok') return { ...base, value: mtu ?? 'OK', detail: 'no fragmentation' }
      return { ...base, value: mtu ?? 'Too small', detail: 'may break VPN or some sites' }
    }
    case 'dns': {
      const via = text.indexOf(' via ')
      const servers = via >= 0 ? text.slice(via + 5).trim() : ''
      const list = servers ? servers.split(',').map((x) => x.trim()).filter(Boolean) : []
      const time = /in ([\d.]+) ms/.exec(text)?.[1] ?? null
      if (c.state !== 'ok') {
        return { ...base, value: 'Failed', detail: ['name resolution failed', list.join(', ')].filter(Boolean).join(' · ') }
      }
      const value = list.length === 0 ? 'Resolves' : list.length === 1 ? list[0]! : `${list.length} servers`
      const detail = [time ? `answers in ${time} ms` : null, list.length > 1 ? list.join(', ') : null].filter(Boolean).join(' · ')
      return { ...base, value, detail }
    }
    case 'captive': {
      if (c.state === 'ok') return { ...base, value: 'None detected', detail: 'clean way out' }
      const rest = text.replace(/^Detected(: | - )?/, '')
      return { ...base, value: 'Detected', detail: rest }
    }
    case 'vpn': {
      if (c.state === 'info') return { ...base, value: 'Active', detail: text.replace(/^Active(: )?/, '') }
      return { ...base, value: 'Not active', detail: 'no VPN adapter up' }
    }
  }
  return { ...base, value: text, detail: '' }
}

/**
 * Cardurile de verificari: cate unul pentru MTU, DNS, portal captiv / proxy,
 * VPN, pe un rand sus in ecran (sub antet, deasupra concluziei). Fara date -
 * ori Connection Quality stins (`off`) - toate gri, "not measured yet".
 */
export function CqCheckCards({ checks, off = false }: { checks: CqCheck[] | null | undefined; off?: boolean }) {
  const by = new Map((checks ?? []).map((c) => [c.kind, c]))
  const cards = CHECK_KINDS.map(({ kind, name }) => {
    const c = by.get(kind)
    return checkCard(!c || off ? { kind, name: c?.name ?? name, state: 'none', text: 'Not measured yet', at: null } : c)
  })
  return (
    <div className="cq-ck" data-section="checks">
      <div className="cq-ck-row" role="list" aria-label="Checks">
        {cards.map((c) => (
          <div key={c.kind} role="listitem" className={`cq-ck-card is-${c.state}`} data-check={c.kind} data-state={c.state} title={`${c.title}: ${c.text}`}>
            <div className="cq-ck-head">
              <span className="cq-ck-title">
                <i className={ICON[c.state]} aria-hidden="true"></i> {c.title}
              </span>
              <span className="cq-ck-state">{c.stateText}</span>
            </div>
            <div className="cq-ck-value">{c.value}</div>
            {c.detail ? <div className="cq-ck-detail">{c.detail}</div> : null}
            {c.at ? <div className="cq-ck-at">{c.at}</div> : null}
          </div>
        ))}
      </div>
      <div className="cq-ck-hint">{CHECKS_HINT}</div>
    </div>
  )
}

/**
 * Verificarile ca o categorie cu titlul ei, ca celelalte din Grid: stau
 * imediat sub concluzie ("All good"), inaintea categoriei Connection
 * (Roxana, 30 septembrie 2026).
 */
export function CqChecksCategory({ checks, off = false }: { checks: CqCheck[] | null | undefined; off?: boolean }) {
  return (
    <section id="cq-cat-checks" className="cq-cat cq-cat-checks" data-category="checks" aria-labelledby="cq-cat-checks-title">
      <h4 className="cq-cat-title" id="cq-cat-checks-title">
        <i className="fas fa-clipboard-check" aria-hidden="true"></i> Checks
      </h4>
      <CqCheckCards checks={checks} off={off} />
    </section>
  )
}

/** Comparatia cu celelalte aparate din sediu (OfficeCompare.php), un rand sub concluzie. */
export function CompareLine({ compare }: { compare: CqCompare }) {
  if (compare.code === 'not_needed' || !compare.text) return null
  const where = compare.scope === 'subnet' ? ' (same subnet)' : compare.scope === 'public_ip' ? ' (same public address)' : ''
  return (
    <div className={`cq-compare is-${compare.code}`} data-compare={compare.code}>
      <i className="fas fa-building" aria-hidden="true"></i> {compare.text}
      {where ? <span className="cq-muted"> {where}</span> : null}
    </div>
  )
}
